import { useState, useEffect } from "react";
import { MessageSquare, Plus, RefreshCw } from "lucide-react";
import { Button } from "../../../components/ui/button";
import { API, getAuthHeaders, formatDateTimeFR } from "./helpers";

export function CommentairesDossier({ dossierId, tenantId, verrouille }) {
  const [commentaires, setCommentaires] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newComment, setNewComment] = useState("");
  const [adding, setAdding] = useState(false);

  useEffect(() => { loadCommentaires(); }, [dossierId]);

  const loadCommentaires = async () => {
    try {
      const headers = getAuthHeaders(tenantId);
      const res = await fetch(`${API}/api/metier/dossiers/${dossierId}/commentaires`, { headers, credentials: 'include' });
      if (res.ok) setCommentaires(await res.json());
    } catch (err) { console.error("Erreur chargement commentaires:", err); }
    finally { setLoading(false); }
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) return;
    setAdding(true);
    try {
      const headers = getAuthHeaders(tenantId, "application/json");
      const userStr = localStorage.getItem("user");
      const user = userStr ? JSON.parse(userStr) : null;
      const auteurNom = user?.name || user?.email || "Utilisateur";
      const res = await fetch(`${API}/api/metier/dossiers/${dossierId}/commentaires`, {
        method: "POST", headers, credentials: 'include',
        body: JSON.stringify({ contenu: newComment, auteur_nom: auteurNom })
      });
      if (res.ok) {
        const comment = await res.json();
        setCommentaires([comment, ...commentaires]);
        setNewComment("");
      }
    } catch (err) { console.error("Erreur ajout commentaire:", err); }
    finally { setAdding(false); }
  };

  const fmtDate = (dateStr) => {
    if (!dateStr) return "-";
    return formatDateTimeFR(dateStr);
  };

  return (
    <div className="bg-[var(--oh-bg-elevated)] border border-[var(--oh-border)] rounded-xl p-6">
      <h3 className="text-[var(--oh-text)] font-medium mb-4">Commentaires du dossier</h3>
      {!verrouille && (
        <div className="flex gap-2 mb-6">
          <textarea placeholder="Ajouter un commentaire..." value={newComment} onChange={(e) => setNewComment(e.target.value)}
            className="flex-1 min-h-[80px] px-3 py-2 bg-[var(--oh-bg-surface)] border border-[var(--oh-border)] rounded-lg text-[var(--oh-text)] placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-purple-500" />
          <Button onClick={handleAddComment} disabled={!newComment.trim() || adding} className="bg-[var(--oh-accent-solid)] hover:bg-[var(--oh-accent-solid)] self-end">
            {adding ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
          </Button>
        </div>
      )}
      {loading ? (
        <div className="flex justify-center py-8"><RefreshCw className="w-6 h-6 text-[var(--oh-text-muted)] animate-spin" /></div>
      ) : commentaires.length === 0 ? (
        <div className="text-center py-8">
          <MessageSquare className="w-12 h-12 mx-auto text-[var(--oh-text-secondary)] mb-3" />
          <p className="text-[var(--oh-text-muted)]">Aucun commentaire</p>
          <p className="text-[var(--oh-text-secondary)] text-sm mt-1">Ajoutez des notes sur ce dossier</p>
        </div>
      ) : (
        <div className="space-y-4">
          {commentaires.map((comment, idx) => (
            <div key={comment.id || idx} className="p-4 bg-[var(--oh-bg-elevated)] rounded-lg">
              <p className="text-[var(--oh-text)] whitespace-pre-wrap">{comment.contenu}</p>
              <div className="flex items-center gap-2 mt-2 text-xs text-[var(--oh-text-muted)]">
                <span>{comment.auteur_nom || "Utilisateur"}</span>
                <span>•</span>
                <span>{fmtDate(comment.created_at)}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
